import { Box, Checkbox, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { GISTMChecklist } from "../../services/api/gistm";
import { HidroDXEditor } from "../edit";
import { Edit, FileOpen } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog, RemoveButton } from "../modals";
import { objectToHash } from "../../core";
import { GISTMCheckItemModal } from "./checkbox_edit_modal";
import { GISTMCheckFileModal } from "./checkbox_files_modal";

export interface GISTMCheckItemProps {
  editable?: boolean;
  onEdit?: (item: GISTMChecklist) => void;
  onRemove?: (item: GISTMChecklist) => void;
  checkData: GISTMChecklist;
}

export const GISTMCheckItem: React.FC<GISTMCheckItemProps> = ({ onEdit, onRemove, checkData, editable }) => {
  const api = useApi();

  const [error, setError] = useState<RequestError>();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openFilesModal, setOpenFilesModal] = useState(false);
  const [checkList, setCheckList] = useState<GISTMChecklist>(checkData);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (starting) {
      setStarting(false);
      return;
    }

    api.gistm.updateChecklist(checkList).then(val => {
      if ("error" in val) {
        setError(val);
        return;
      }

      if (objectToHash({ ...val, dataModificacao: undefined }) !== objectToHash({ ...checkList, dataModificacao: undefined })) {
        setCheckList(val);
      }
    });

  }, [checkList]);


  const onCheck = (ev: React.ChangeEvent<HTMLInputElement>, check: boolean) => {
    ev.preventDefault();
    ev.stopPropagation();

    setCheckList(prev => ({ ...prev, checked: check }));
  };

  const onDelete = () => {
    api.gistm.removeChecklist(checkData.id).then(val => {
      if ("error" in val) {
        setError(val);
        return;
      }
      onRemove?.(val);
    });
  };


  return <>
    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    {openEditModal && <GISTMCheckItemModal
      checkData={checkList}
      open={Boolean(openEditModal)}
      barragem={checkList.idBarragem}
      onClose={() => setOpenEditModal(false)}
      onCancel={() => setOpenEditModal(false)}
      onFinish={() => {
        onEdit?.(checkList);
        setOpenEditModal(false);
      }}
    />}

    {openFilesModal && <GISTMCheckFileModal
      checkData={checkList}
      open={Boolean(openFilesModal)}
      onClose={() => setOpenFilesModal(false)}
      onFinish={(files) => {
        setCheckList(prev => ({ ...prev, files }));
        checkList.files = files;
        onEdit?.(checkList);
        setOpenFilesModal(false);
      }}
    />}

    <Paper elevation={2} sx={{
      py: "5px",
      px: "15px",
      gap: "5px",
      width: "100%",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    }}>

      <Checkbox
        key={objectToHash(checkList)}
        checked={checkList.checked ?? false}
        onChange={onCheck}
        sx={{ width: "65px", height: "65px" }}
      />

      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: 'column',
      }}>
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: 'row',
          alignItems: "center",
        }}>
          {checkList.principio > 0 && <Typography fontWeight={"bold"}>{checkList.principio}.{checkList.requisito}&nbsp;-&nbsp;</Typography>}
          <Typography>{checkList.title}</Typography>

          <div style={{ display: "flex", flex: 1 }} />

          <Tooltip title="Arquivos">
            <IconButton onClick={() => setOpenFilesModal(true)}>
              <FileOpen />
            </IconButton>
          </Tooltip>

          {editable && <Tooltip title="Editar CheckItem">
            <IconButton onClick={() => setOpenEditModal(true)}>
              <Edit />
            </IconButton>
          </Tooltip>}

          {editable && <RemoveButton
            title="Remover Item do Checklist"
            message="Esta ação é irreversível!"
            onRemove={onDelete}
          />}
        </Box>

        {checkList.desc && <Divider flexItem />}

        {checkList.desc &&
          <div style={{ textAlign: "justify" }}>
            <HidroDXEditor readOnly startText={checkList.desc} />
          </div>
        }
      </Box>
    </Paper>
  </>;
};